import React, { useState } from 'react';
import type { FormEvent } from 'react';
import type { RecoverPasswordViewProps } from './RecoverPasswordView.types';
import { EnvelopeIcon } from './icons';

/**
 * RecoverPasswordView - Vista de recuperación de contraseña del sistema Siesa
 *
 * Vista completa para restablecer contraseña que incluye:
 * - Logo de Siesa
 * - Campo de email para enviar instrucciones
 * - Botón para regresar al login
 * - Fondo decorativo opcional
 *
 * Mejores prácticas implementadas:
 * - Orden de modificadores: {responsive}:{dark}:{state}:{utility}
 * - Dark mode con estrategia 'class' (darkMode: 'class')
 * - Tokens de color consistentes con la documentación
 * - Type safety con TypeScript estricto
 * - Accesibilidad con ARIA labels y manejo de formulario semántico
 * - Validación de email básica
 *
 * @see docs/colors.md - Sistema de colores
 * @see docs/typography.md - Sistema tipográfico
 * @see docs/spacing.md - Sistema de espaciado
 * @see docs/shadows.md - Sistema de sombras
 *
 * @example
 * ```tsx
 * <RecoverPasswordView
 *   onSubmit={(email) => console.log('Recover password for', email)}
 *   onBackToLogin={() => console.log('Back to login')}
 * />
 * ```
 */
export const RecoverPasswordView: React.FC<RecoverPasswordViewProps> = ({
  onSubmit,
  onBackToLogin,
  isLoading = false,
  errorMessage,
  successMessage,
  className = '',
  showBackground = true,
  variant = 'responsive',
}) => {
  // ===== ESTADO LOCAL =====
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');

  // ===== HANDLERS =====
  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Validación básica de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailError('Por favor ingresa un email válido');
      return;
    }

    setEmailError('');
    onSubmit?.(email);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (emailError) setEmailError('');
  };

  return (
    <div
      className={[
        'relative',
        'min-h-screen',
        'w-full',
        'flex',
        'flex-col',
        'items-center',
        'justify-center',
        'box-border',
        // Mobile: sin padding en el wrapper (el padding está en el contenedor)
        'p-0',
        // Desktop: padding para centrar el card
        'md:p-4',
        className,
      ].join(' ')}
    >
      {/* ===== FONDO DECORATIVO (solo desktop) ===== */}
      {showBackground && (
        <div className="hidden md:block absolute inset-0 pointer-events-none" aria-hidden="true">
          <div className="absolute inset-0 bg-primary-100 dark:bg-dark-bg-custom" />
          <img
            src="/bg_siesa.jpg"
            alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-10"
          />
        </div>
      )}

      {/* ===== CONTENEDOR PRINCIPAL ===== */}
      <div
        className={[
          // Clases base comunes
          'relative',
          'box-border',
          'flex',
          'flex-col',
          'items-center',
          'justify-center',
          'gap-4',
          'p-16',
          'shrink-0',
          'bg-bg-primary',
          'dark:bg-zinc-700',

          // Clases específicas según variant
          ...(variant === 'mobile' ? [
            // Forzar mobile: sin efectos de card
            'w-full',
            'h-full',
            'shadow-none',
            'rounded-none',
            'border-0',
          ] : variant === 'desktop' ? [
            // Forzar desktop: con efectos de card
            'w-[400px]',
            'h-auto',
            'rounded-2xl',
            'shadow-lg',
            'dark:shadow-2xl',
            'overflow-clip',
            'border',
            'border-transparent',
            'dark:border-dark-border-primary',
          ] : [
            // Responsive: cambia según viewport
            'w-full',
            'h-full',
            'shadow-none',
            'rounded-none',
            'border-0',
            'md:w-[400px]',
            'md:h-auto',
            'md:rounded-2xl',
            'md:shadow-lg',
            'md:dark:shadow-2xl',
            'md:overflow-clip',
            'md:border',
            'md:border-transparent',
            'md:dark:border-dark-border-primary',
          ]),
        ].join(' ')}
      >
        {/* ===== LOGO ===== */}
        <div className="w-full flex flex-col items-center pb-4">
          <img
            src="/.Siesa Logo.png"
            alt="Siesa"
            className="w-[200px] h-[50px] object-contain"
          />
        </div>

        {/* ===== HEADING ===== */}
        <div className="w-full text-center">
          <h1
            className={[
              'text-[20px]',
              'leading-[28px]',
              'font-bold',
              'tracking-[-0.5px]',
              'text-content-primary',
              'dark:text-dark-content-primary',
            ].join(' ')}
          >
            Restablecer Contraseña
          </h1>
          <p
            className={[
              'text-[12px]',
              'leading-[16px]',
              'font-normal',
              'text-content-primary',
              'dark:text-dark-content-primary',
            ].join(' ')}
          >
            Reestablece tu contraseña ingresando con tu correo electrónico.
          </p>
        </div>

        {/* ===== MENSAJE DE ERROR GENERAL ===== */}
        {errorMessage && (
          <div
            className={[
              'w-full',
              'p-3',
              'bg-error-bg',
              'dark:bg-error-bg',
              'border',
              'border-error-border',
              'dark:border-error-border',
              'rounded-md',
              'text-error-content',
              'dark:text-error-content',
              'text-sm',
            ].join(' ')}
            role="alert"
          >
            {errorMessage}
          </div>
        )}

        {/* ===== MENSAJE DE ÉXITO ===== */}
        {successMessage && (
          <div
            className={[
              'w-full',
              'p-3',
              'bg-[#d1fae5]',
              'dark:bg-[#064e3b]',
              'border',
              'border-[#a7f3d0]',
              'dark:border-[#065f46]',
              'rounded-md',
              'text-[#065f46]',
              'dark:text-[#a7f3d0]',
              'text-sm',
            ].join(' ')}
            role="status"
          >
            {successMessage}
          </div>
        )}

        {/* ===== FORMULARIO ===== */}
        <form onSubmit={handleSubmit} className="w-full flex flex-col gap-2">
          {/* Email Input */}
          <div className="flex flex-col gap-2 w-full">
            <label
              htmlFor="email-input"
              className="font-bold text-sm leading-5 text-content-primary dark:text-dark-content-primary"
            >
              Correo Electrónico <span className="text-red-600">*</span>
            </label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none opacity-50">
                <EnvelopeIcon className="w-4 h-4 text-content-tertiary dark:text-dark-content-tertiary" />
              </div>
              <input
                id="email-input"
                type="email"
                placeholder="ejemplo@miempresa.com"
                value={email}
                onChange={handleEmailChange}
                disabled={isLoading}
                required
                autoComplete="email"
                className={[
                  'w-full',
                  'pl-9',
                  'pr-3',
                  'py-1.5',
                  'text-base',
                  'leading-6',
                  'bg-bg-primary',
                  'dark:bg-zinc-700',
                  'border',
                  'border-border-primary',
                  'dark:border-dark-border-primary',
                  'rounded-lg',
                  'placeholder:text-content-tertiary',
                  'dark:placeholder:text-dark-content-tertiary',
                  'text-content-primary',
                  'dark:text-dark-content-primary',
                  'focus:outline-hidden',
                  'focus:border-primary-600',
                  'dark:focus:border-dark-border-custom',
                  'focus:ring-2',
                  'focus:ring-primary-400',
                  'dark:focus:ring-dark-border-custom',
                  'transition-all',
                  'duration-150',
                  emailError ? 'border-error-border!' : '',
                ].filter(Boolean).join(' ')}
              />
            </div>
            {emailError && (
              <p className="text-xs text-error-content dark:text-error-content">
                {emailError}
              </p>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading || !email}
            className={[
              'relative',
              'w-full',
              'px-2.5',
              'py-1.5',
              'flex',
              'items-center',
              'justify-center',
              'gap-2',
              'font-bold',
              'text-sm',
              'leading-5',
              'text-primary-inverse-content',
              'dark:text-[#0e79fd]',
              'bg-[#0e79fd]',
              'dark:bg-dark-bg-inverse',
              'border',
              'border-[#3c9bf6]',
              'dark:border-[#93d1fd]',
              'rounded-md',
              'shadow-button-inset',
              'hover:bg-primary-500',
              'dark:hover:bg-[#93d1fd]',
              'active:scale-95',
              'transition-all',
              'duration-150',
              'focus:outline-hidden',
              'focus:ring-2',
              'focus:ring-primary-400',
              'dark:focus:ring-[#93d1fd]',
              'focus:ring-offset-2',
              'dark:focus:ring-offset-[#3f3f46]',
              'disabled:opacity-50',
              'disabled:cursor-not-allowed',
            ].join(' ')}
          >
            {isLoading ? 'Enviando...' : 'Reestablecer Contraseña'}
          </button>
        </form>

        {/* ===== BACK TO LOGIN BUTTON ===== */}
        <button
          type="button"
          onClick={onBackToLogin}
          className={[
            'w-full',
            'px-2.5',
            'py-1.5',
            'flex',
            'items-center',
            'justify-center',
            'gap-2',
            'font-bold',
            'text-sm',
            'leading-5',
            'text-[#0e79fd]',
            'dark:text-[#93d1fd]',
            'bg-transparent',
            'border',
            'border-[#93d1fd]',
            'dark:border-[#93d1fd]',
            'rounded-md',
            'hover:bg-primary-100',
            'dark:hover:bg-[#93d1fd]/10',
            'transition-all',
            'duration-150',
            'focus:outline-hidden',
            'focus:ring-2',
            'focus:ring-primary-400',
            'dark:focus:ring-[#93d1fd]',
            'focus:ring-offset-2',
            'dark:focus:ring-offset-[#3f3f46]',
          ].join(' ')}
        >
          Iniciar Sesión
        </button>
      </div>
    </div>
  );
};
